import { useEffect, useMemo, useState } from "react";

import { type AssetType, EntityInvoicingModes } from "@doitintl/cmp-models";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import uniq from "lodash/uniq";

import { billingProfilesTexts } from "../../../../assets/texts";
import { type Entity } from "../../../../Context/customer/EntitiesContext";
import { type BillingProfileInvoiceBucket, type EditBillingProfileData } from "../../BillingProfileForm.models";
import { useEntityAllowedAssetsCollection } from "../../useEntityAllowedAssetsCollection";
import { PanelWrapper } from "../common/PanelWrapper";
import BucketCard from "../components/invoice-settings/BucketCard";
import BucketCreateForm from "../components/invoice-settings/BucketCreateForm";
import BucketCreatePlaceholder from "../components/invoice-settings/BucketCreatePlaceholder";
import BucketEditForm from "../components/invoice-settings/BucketEditForm";
import DeleteBucketDialog from "../components/invoice-settings/DeleteBucketDialog";
import DeleteDefaultBucketDialog from "../components/invoice-settings/DeleteDefaultBucketDialog";
import { BUCKET_TEXTS } from "../components/invoice-settings/texts";
import { useBucketsCRUD } from "../components/invoice-settings/useBucketsCRUD";
import { mapAssetTypeToBucketType } from "../components/invoice-settings/utils";
import { scrollToFormElement } from "../utils";

export const INVOICE_SETTINGS_PANEL_ID = "invoice-settings";

export const InvoiceSettingsPanel = ({
  entity,
  setIsInvalid,
  disabledBucketing,
}: {
  entity: Entity;
  setIsInvalid: (isInvalid: boolean) => void;
  disabledBucketing: boolean;
}) => {
  const {
    values: { invoicingMode, autoAssignGCP, buckets, invoicePerService, separateInvoice },
    handleChange,
    setFieldValue,
  } = useFormikContext<EditBillingProfileData>();

  const [bucketCreateFormParams, setBucketCreateFormParams] = useState<{ open: boolean; assetType: AssetType | null }>({
    open: false,
    assetType: null,
  });

  const [bucketToEdit, setBucketToEdit] = useState<BillingProfileInvoiceBucket | null>(null);
  const [bucketToDelete, setBucketToDelete] = useState<BillingProfileInvoiceBucket | null>(null);

  const bucketDefaultOptionsOnDelete = useMemo(
    () => buckets.filter((bucket) => bucket !== bucketToDelete && bucket.type === bucketToDelete?.type),
    [bucketToDelete, buckets]
  );

  const [availableAssets] = useEntityAllowedAssetsCollection(entity);

  const unassignedAssets = useMemo(() => {
    const alreadyAssignedAssetIds = new Set(
      buckets.flatMap(({ assets }: { assets: Array<{ id: string }> }) => assets.map(({ id }) => id))
    );

    return availableAssets.filter(({ id }) => !alreadyAssignedAssetIds.has(id));
  }, [availableAssets, buckets]);

  const defaultBuckets: Partial<Record<AssetType, boolean>> = useMemo(
    () =>
      buckets.reduce((map, bucket) => {
        if (bucket.isDefault) {
          map[bucket.type] = true;
        }
        return map;
      }, {}),
    [buckets]
  );

  const { assignAssetsToBucket, createBucket, deleteBucket, setBucketAsDefault, setBucketName } = useBucketsCRUD(
    buckets,
    setFieldValue
  );

  const availableBucketTypes = useMemo(
    () => uniq(availableAssets.map(({ data: { type } }) => mapAssetTypeToBucketType(type)).filter((type) => !!type)),
    [availableAssets]
  );

  const assetsMissingDefaultBucket = useMemo(
    () => availableBucketTypes.filter((bucketType) => !defaultBuckets[bucketType]),
    [availableBucketTypes, defaultBuckets]
  );

  useEffect(() => {
    if (invoicingMode === EntityInvoicingModes.CUSTOM) {
      setIsInvalid(!!assetsMissingDefaultBucket.length);
      return;
    }

    setIsInvalid(false);
  }, [assetsMissingDefaultBucket.length, invoicingMode, setIsInvalid]);

  const openBucketCreateForm = (assetType: AssetType) => {
    setBucketCreateFormParams({ open: true, assetType });
  };

  const openBucketEditForm = (bucket: BillingProfileInvoiceBucket) => {
    setBucketToEdit(bucket);
  };

  const sortedBuckets = useMemo(
    () => buckets.toSorted((a, b) => (a.type === b.type ? 0 : a.type > b.type ? 1 : -1)),
    [buckets]
  );

  return (
    <PanelWrapper title="Invoice settings" id={INVOICE_SETTINGS_PANEL_ID} paddingTop={9}>
      <FormControl sx={{ marginBottom: 25 }}>
        <FormLabel sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ color: "text.primary", fontWeight: "medium" }}>
            General
          </Typography>
        </FormLabel>
        <RadioGroup aria-label="Invoice Mode" name="invoicingMode" value={invoicingMode} onChange={handleChange}>
          <FormControlLabel
            value={EntityInvoicingModes.GROUP}
            control={<Radio />}
            label={<Typography variant="body1">Separate invoice by asset type</Typography>}
          />
          <FormControlLabel
            onClick={() => {
              setTimeout(() => {
                scrollToFormElement(INVOICE_SETTINGS_PANEL_ID);
              });
            }}
            value={EntityInvoicingModes.CUSTOM}
            control={<Radio />}
            label={<Typography variant="body1">Invoice using custom bucketing</Typography>}
            disabled={disabledBucketing}
            sx={{
              opacity: disabledBucketing ? 0.5 : 1,
            }}
          />
        </RadioGroup>
        {invoicingMode === EntityInvoicingModes.CUSTOM && !disabledBucketing && (
          <>
            {bucketCreateFormParams.open && (
              <BucketCreateForm
                promptedAssetType={bucketCreateFormParams.assetType}
                availableBucketTypes={availableBucketTypes}
                assetsMissingDefaultBucket={assetsMissingDefaultBucket}
                onClose={() => {
                  setBucketCreateFormParams({ open: false, assetType: null });
                }}
                createBucket={createBucket}
              />
            )}

            {bucketToEdit && (
              <BucketEditForm
                bucket={bucketToEdit}
                onClose={() => {
                  setBucketToEdit(null);
                }}
                updateBucketName={(name) => {
                  setBucketName(bucketToEdit, name);
                }}
              />
            )}

            {bucketToDelete ? (
              bucketToDelete.isDefault && !!bucketDefaultOptionsOnDelete.length ? (
                <DeleteDefaultBucketDialog
                  bucketToDelete={bucketToDelete}
                  bucketDefaultOptions={bucketDefaultOptionsOnDelete}
                  deleteBucket={deleteBucket}
                  onClose={() => {
                    setBucketToDelete(null);
                  }}
                />
              ) : (
                <DeleteBucketDialog
                  bucketToDelete={bucketToDelete}
                  deleteBucket={deleteBucket}
                  onClose={() => {
                    setBucketToDelete(null);
                  }}
                />
              )
            ) : null}

            <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 3, mb: 1 }}>
              Custom bucketing settings
            </Typography>
            <FormControlLabel
              label={billingProfilesTexts.AUTO_ASSIGN_NEW_GCP_PROJECTS_LABEL}
              control={<Checkbox name="autoAssignGCP" checked={autoAssignGCP} onChange={handleChange} />}
              sx={{ marginBottom: 2 }}
            />
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>
                Invoice buckets
              </Typography>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={() => {
                  setBucketCreateFormParams({ open: true, assetType: null });
                }}
              >
                Create new invoice bucket
              </Button>
            </Stack>

            <Typography variant="body1" sx={{ color: "text.secondary", my: 2 }}>
              {BUCKET_TEXTS.DESCRIPTION}
            </Typography>

            <Stack spacing={2}>
              {assetsMissingDefaultBucket.map((assetType) => (
                <BucketCreatePlaceholder
                  key={assetType}
                  assetType={assetType}
                  openCreateBucketForm={openBucketCreateForm}
                />
              ))}

              {sortedBuckets.map((bucket) => (
                <BucketCard
                  key={bucket._id}
                  bucket={bucket}
                  openBucketEditForm={openBucketEditForm}
                  unassignedAssets={unassignedAssets}
                  assignAssetsToBucket={assignAssetsToBucket}
                  openDeleteBucketDialog={(bucket) => {
                    setBucketToDelete(bucket);
                  }}
                  setBucketAsDefault={setBucketAsDefault}
                />
              ))}
            </Stack>
          </>
        )}
      </FormControl>
      <FormControl sx={{ marginBottom: 25 }}>
        <FormLabel sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ color: "text.primary", fontWeight: "medium" }}>
            Marketplace
          </Typography>
        </FormLabel>
        <FormControlLabel
          control={<Checkbox checked={separateInvoice} name="separateInvoice" onChange={handleChange} />}
          label="Separate Marketplace purchases"
        />
        <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
          <RadioGroup
            name="invoicePerService"
            value={invoicePerService}
            onChange={(e) => setFieldValue("invoicePerService", e.target.value === "true")}
          >
            <FormControlLabel
              label="Create individual invoices for each purchase"
              control={<Radio />}
              value={true}
              disabled={!separateInvoice}
            />
            <FormControlLabel
              label="Create a single invoice with line items for each purchase"
              control={<Radio />}
              value={false}
              disabled={!separateInvoice}
            />
          </RadioGroup>
        </Box>
      </FormControl>
    </PanelWrapper>
  );
};
